import { ESCAPE, hasModifierKey } from '@angular/cdk/keycodes';
import { ComponentType } from '@angular/cdk/portal';
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { Observable, isObservable, of } from 'rxjs';
import { catchError, filter, finalize, take, takeUntil } from 'rxjs/operators';
import { SideSheetCloseGuard, isSideSheetCloseGuard } from './side-sheet-close-guard';

const DEFAULT_SIDE_SHEET_WIDTH = '396px';
const SIDE_SHEET_HEIGHT = '100vh';
const MAXIMUM_SIDE_SHEET_WIDTH = '92vw';

/**
 * Geometry a body declares for itself, as `static readonly sideSheetOptions`. A wizard's width
 * belongs to the wizard, not to each of its N call sites. Read before opening so it lands in the
 * initial config with no resize flash; an explicit caller config still wins.
 */
export interface SideSheetOptions {
  width?: string;
  isFillHeight?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class SideSheetService {
  /** Sheets whose close guard is currently being asked — stops a second Escape re-entering. */
  private sheetIdsAwaitingCloseDecision: string[] = [];

  constructor(private dialog: MatDialog) {}

  /**
   * Opens any component as a right-anchored, full-height sheet.
   *
   * Pass `config.viewContainerRef` when the body is declared only in a lazy-loaded module, or
   * relies on providers from the calling component's tree. This service is `providedIn: 'root'`,
   * so without it component resolution starts at the root injector and such bodies throw
   * "No component factory found". Supplying it also makes the sheet a logical child of the
   * caller, so destroying the caller destroys the sheet.
   */
  public open<TBodyComponent, TData, TResult>(
    bodyComponent: ComponentType<TBodyComponent>,
    data?: TData,
    callerConfig?: MatDialogConfig<TData>
  ): MatDialogRef<TBodyComponent, TResult> {
    const config = this.buildConfig(bodyComponent, data, callerConfig);
    const sheetReference = this.dialog.open<TBodyComponent, TData, TResult>(bodyComponent, config);
    this.attachCloseGuardIfBodyDefinesOne(sheetReference);
    return sheetReference;
  }

  /**
   * The single exit funnel — Escape, backdrop click, and the body's own close button all arrive
   * here. An unguarded body closes immediately, so a sheet that opts into nothing behaves exactly
   * like a plain dialog.
   */
  public requestClose<TResult>(
    sheetReference: MatDialogRef<any, TResult>,
    result?: TResult
  ): void {
    if (!sheetReference || sheetReference.getState() !== MatDialogState.OPEN) {
      return;
    }

    const bodyInstance = sheetReference.componentInstance;
    if (!isSideSheetCloseGuard(bodyInstance)) {
      sheetReference.close(result);
      return;
    }

    if (this.sheetIdsAwaitingCloseDecision.indexOf(sheetReference.id) > -1) {
      return;
    }
    this.sheetIdsAwaitingCloseDecision.push(sheetReference.id);

    this.toCloseDecision(bodyInstance)
      .pipe(
        // A guard that never completes would otherwise hold the latch forever.
        take(1),
        // Close on a broken guard: the alternative is an un-dismissable overlay with no Escape,
        // no backdrop and no working button.
        catchError(() => of(true)),
        finalize(() => this.forgetCloseDecision(sheetReference.id))
      )
      .subscribe(isCloseAllowed => {
        // Re-check the state: a late Escape during the exit animation would otherwise overwrite a
        // real result with undefined, since close() overwrites the result unconditionally.
        if (isCloseAllowed && sheetReference.getState() === MatDialogState.OPEN) {
          sheetReference.close(result);
        }
      });
  }

  /**
   * Pushes data into an already-open sheet. Takes a callback rather than merging properties,
   * because assigning to a portal-attached component does not fire ngOnChanges. Returns false if
   * the sheet has already detached — Material nulls componentInstance at that point.
   */
  public pushToBody<TBodyComponent>(
    sheetReference: MatDialogRef<TBodyComponent, any>,
    applyToBody: (bodyInstance: TBodyComponent) => void
  ): boolean {
    if (!sheetReference) {
      return false;
    }
    const bodyInstance = sheetReference.componentInstance;
    if (!bodyInstance) {
      return false;
    }
    applyToBody(bodyInstance);
    return true;
  }

  /**
   * Resizes an open sheet. Always passes height, because updateSize(width = '', height = '')
   * wipes the height when called with width alone, collapsing the panel to content height.
   */
  public resize(sheetReference: MatDialogRef<any, any>, width: string): void {
    if (!sheetReference) {
      return;
    }
    sheetReference.updateSize(width, SIDE_SHEET_HEIGHT);
  }

  private buildConfig<TBodyComponent, TData>(
    bodyComponent: ComponentType<TBodyComponent>,
    data: TData,
    callerConfig: MatDialogConfig<TData>
  ): MatDialogConfig<TData> {
    const bodyOptions = this.readBodyOptions(bodyComponent);

    const panelClasses = ['csi-side-sheet'];
    if (bodyOptions.isFillHeight) {
      panelClasses.push('csi-side-sheet-fill');
    }

    const config: MatDialogConfig<TData> = {
      panelClass: panelClasses,
      width: bodyOptions.width ? bodyOptions.width : DEFAULT_SIDE_SHEET_WIDTH,
      // Always leave a sliver of backdrop on a narrow viewport, so a fixed pixel width cannot
      // fill the screen edge to edge. Overridable per caller.
      maxWidth: MAXIMUM_SIDE_SHEET_WIDTH,
      height: SIDE_SHEET_HEIGHT,
      maxHeight: SIDE_SHEET_HEIGHT,
      // Anchors the panel flush right without a transform override, so Material's own exit
      // animation still runs and focus is restored to whatever opened the sheet.
      position: { right: '0', top: '0' },
      autoFocus: true,
      restoreFocus: true
    };

    // Only defined values overwrite a default, so an explicitly passed `undefined` cannot blank
    // one out. Caller config beats the body's declared options.
    this.copyDefinedValues(config, callerConfig);
    config.panelClass = this.mergePanelClasses(panelClasses, callerConfig);

    // Set only when supplied, so a caller passing data through `config.data` is not clobbered.
    if (data !== undefined) {
      config.data = data;
    }
    return config;
  }

  /** A body without `sideSheetOptions` gets the defaults — the property is entirely optional. */
  private readBodyOptions<TBodyComponent>(
    bodyComponent: ComponentType<TBodyComponent>
  ): SideSheetOptions {
    const declaredOptions: SideSheetOptions = (bodyComponent as any).sideSheetOptions;
    return declaredOptions ? declaredOptions : {};
  }

  private copyDefinedValues(target: any, source: any): void {
    if (!source) {
      return;
    }
    Object.keys(source).forEach(configKey => {
      if (source[configKey] !== undefined) {
        target[configKey] = source[configKey];
      }
    });
  }

  /** Keeps the sheet's own panel classes even when a caller supplies its own. */
  private mergePanelClasses(panelClasses: string[], callerConfig: MatDialogConfig<any>): string[] {
    if (!callerConfig || !callerConfig.panelClass) {
      return panelClasses;
    }
    const callerClasses =
      typeof callerConfig.panelClass === 'string'
        ? [callerConfig.panelClass]
        : callerConfig.panelClass;
    return panelClasses.concat(callerClasses.filter(name => panelClasses.indexOf(name) === -1));
  }

  /**
   * Arms the guard only for bodies that define one. For every other body nothing below ever runs,
   * which is what keeps an opted-out sheet byte-identical to a plain dialog.
   */
  private attachCloseGuardIfBodyDefinesOne(sheetReference: MatDialogRef<any, any>): void {
    if (!isSideSheetCloseGuard(sheetReference.componentInstance)) {
      return;
    }

    // Suppresses Material's own Escape and backdrop handling: both re-read disableClose at event
    // time, so setting it after open() routes every exit through requestClose instead.
    sheetReference.disableClose = true;

    sheetReference
      .keydownEvents()
      .pipe(
        filter(
          keyboardEvent =>
            keyboardEvent.keyCode === ESCAPE &&
            !hasModifierKey(keyboardEvent) &&
            // An ng-select dropdown calls preventDefault() without stopPropagation(), so its
            // Escape reaches the sheet. Ignoring handled events keeps that Escape with the
            // dropdown, on the guarded path only.
            !keyboardEvent.defaultPrevented
        ),
        takeUntil(sheetReference.afterClosed())
      )
      .subscribe(keyboardEvent => {
        keyboardEvent.preventDefault();
        this.requestClose(sheetReference);
      });

    sheetReference
      .backdropClick()
      .pipe(takeUntil(sheetReference.afterClosed()))
      .subscribe(() => this.requestClose(sheetReference));
  }

  private toCloseDecision(bodyInstance: SideSheetCloseGuard): Observable<boolean> {
    const closeDecision = bodyInstance.canCloseSideSheet();
    return isObservable(closeDecision) ? closeDecision : of(!!closeDecision);
  }

  private forgetCloseDecision(sheetId: string): void {
    const latchIndex = this.sheetIdsAwaitingCloseDecision.indexOf(sheetId);
    if (latchIndex > -1) {
      this.sheetIdsAwaitingCloseDecision.splice(latchIndex, 1);
    }
  }
}
